// Services
import { ErrorCategory, handleError } from '@services/errorService';

// Graph
import { authGroupList } from '@graphql/queries/group';

// Api
import client from '@api';

// Types
import type {
  AuthGroup,
  GqlQueryRootAuthGroupListArgs,
} from '@app-types/index';
import type { AuthGroupListResponse } from './types';

/* Module-level shared store for the active organization's auth groups (saved
 * lists).
 *
 * The groups list is slow-changing, so it's fetched once per organization and
 * reused by every useGroups() instance — drawers, pages, menus — instead of
 * refiring `AuthGroupList` on each mount. Reads go through useSyncExternalStore
 * so all consumers stay in sync.
 *
 * Freshness: mutations made through useGroups push their result here via
 * `setGroups`, updating every consumer live with no refetch. Group changes made
 * OUTSIDE useGroups (list create/rename/delete, auth removal) call `invalidate`
 * to refetch once and re-broadcast — so the cached list never goes stale. */

type Listener = () => void;
type GroupsUpdater = AuthGroup[] | ((prev: AuthGroup[]) => AuthGroup[]);

let groups: AuthGroup[] = [];
let loading = true;
let loaded = false;
let inflight: Promise<void> | null = null;
let organizationId: string | null = null;
let loadGeneration = 0;
const listeners = new Set<Listener>();

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

// Single fetch, replicating the original query verbatim (the `AuthGroupList`
// page returns the full set; the legacy code's `hasMore` loop never advanced a
// cursor, so a one-shot fetch is equivalent and avoids a non-terminating loop).
const fetchGroups = async (): Promise<AuthGroup[]> => {
  const { data } = await client().query<
    AuthGroupListResponse,
    GqlQueryRootAuthGroupListArgs
  >({
    query: authGroupList,
    variables: { input: { listViewParams: { pagination: {} } } },
    fetchPolicy: 'no-cache',
  });
  return data?.authGroupList?.items ?? [];
};

const load = (nextOrganizationId: string | null): Promise<void> => {
  const generation = ++loadGeneration;
  const organizationChanged = organizationId !== nextOrganizationId;
  organizationId = nextOrganizationId;
  loaded = false;
  loading = true;
  if (organizationChanged) groups = [];
  emit();
  inflight = fetchGroups()
    .then((nextGroups) => {
      if (generation !== loadGeneration) return;
      groups = nextGroups;
      loaded = true;
    })
    .catch((error) => {
      if (generation !== loadGeneration) return;
      handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
    })
    .finally(() => {
      if (generation !== loadGeneration) return;
      loading = false;
      inflight = null;
      emit();
    });
  return inflight;
};

export const groupsStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getGroups(): AuthGroup[] {
    return groups;
  },

  getLoading(): boolean {
    return loading;
  },

  setGroups(updater: GroupsUpdater): void {
    groups =
      typeof updater === 'function'
        ? (updater as (prev: AuthGroup[]) => AuthGroup[])(groups)
        : updater;
    emit();
  },

  // Fetch once per organization. Switching organizations clears the previous
  // snapshot immediately and invalidates any response still in flight for it.
  ensureLoaded(nextOrganizationId: string | null = null): Promise<void> {
    if (organizationId !== nextOrganizationId) {
      return load(nextOrganizationId);
    }
    if (loaded) return Promise.resolve();
    if (inflight) return inflight;
    return load(nextOrganizationId);
  },

  // Force a refetch after a group change made outside useGroups, then broadcast
  // the fresh list to every mounted consumer.
  invalidate(): Promise<void> {
    return load(organizationId);
  },

  // Clear all module state — for test isolation (the store is a singleton, so
  // state would otherwise leak between test cases).
  reset(): void {
    loadGeneration += 1;
    groups = [];
    loading = true;
    loaded = false;
    inflight = null;
    organizationId = null;
    listeners.clear();
  },
};

export default groupsStore;
